import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

// Last-resort guard so a bug in any one render (bad chart spec, etc.)
// doesn't leave the whole page blank with only the dev console to
// explain what happened.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);
    return (
      <div className="m-6 rounded-[15px] border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <div className="font-medium mb-1">Something broke rendering this view.</div>
        <div className="font-mono text-xs whitespace-pre-wrap">{this.state.error.message}</div>
        <button
          type="button"
          onClick={this.reset}
          className="mt-3 text-xs underline hover:text-red-900"
        >
          Try again
        </button>
      </div>
    );
  }
}
